import DialogFullScreen from '../../../../../components/DialogFullScreen';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { DialogTitle } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useMemo } from 'react';
import DialogCadastrarCategoria from './components/DialogCadastrarCategoria';
import axiosClient from '../../../../../lib/axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import DetalheCategoria from './components/DetalheCategoria';

interface IDialogListarCategorias {
  open: boolean;
  handleClose: () => void;
}

interface ISubCategoria {
  id: number;
  categoriaId: number;
  nome: string;
  atributos: Atributo[];
  ordem: string[];
  subcategorias?: ISubCategoria[];
}
interface Atributo {
  id: number;
  categoria_id: number;
  nome: string;
  descricao: string;
  compoe_nome: boolean;
  obrigatorio: boolean;
  tipo: string;
}

interface ICategoria {
  id: number;
  nome: string;
  atributos: Atributo[];
  categoria_atributos: Atributo[];
  ordem: string[];
  subcategorias: ISubCategoria[];
}

export function DialogListarCategorias({ open, handleClose }: IDialogListarCategorias) {
  const [selected, setSelected] = useState<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [cadastrar, setCadastrar] = useState<boolean>(false);
  const [editedAtributos, setEditedAtributos] = useState<{ [key: number]: string[] }>({});
  const [editedOrdem, setEditedOrdem] = useState<{ [key: number]: string[] }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: categoriasData, refetch: refetchCategorias } = useQuery(
    ['categorias'],
    async () => {
      const response = await axiosClient.get('/estoque/categoria/listar');
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const categorias = useMemo(() => categoriasData?.data || [], [categoriasData]);

  const openDialogCadastrar = () => {
    setCadastrar(true);
  };

  const closeDialogCadastrar = () => {
    setCadastrar(false);
  };

  const selectedData = useMemo(() => {
    if (!selected) return null;

    const findInTree = (items: (ICategoria | ISubCategoria)[], targetId: number, parentHierarchy: any[] = []): any => {
      for (const item of items) {
        const currentHierarchy = [...parentHierarchy, {
          id: item.id,
          categoriaId: (item as ISubCategoria).categoriaId,
          nome: item.nome,
          atributos: item.atributos
        }];

        if (item.id === targetId) {
          return {
            id: item.id,
            categoriaId: (item as ISubCategoria).categoriaId,
            nome: item.nome,
            atributos: item.atributos,
            ordem: item.ordem,
            hierarchy: currentHierarchy
          };
        }

        if (item.subcategorias && item.subcategorias.length > 0) {
          const found = findInTree(item.subcategorias, targetId, currentHierarchy);
          if (found) return found;
        }
      }
      return null;
    };

    return findInTree(categorias, selected);
  }, [selected, categorias]);


  const handleEdit = (categoriaId: number) => {
    setEditing(categoriaId);
    if (selectedData) {
      setEditedAtributos(prev => ({
        ...prev,
        [categoriaId]: [...selectedData.atributos]
      }));
      setEditedOrdem(prev => ({
        ...prev,
        [categoriaId]: [...selectedData.ordem]
      }));
    }
  };

  const { mutate: updateCategoria } = useMutation(
    async (categoriaId: number) => {
      const dadosAtualizados = {
        id: categoriaId,
        nome: selectedData?.nome,
        atributos: editedAtributos[categoriaId] || [],
        ordem: editedOrdem[categoriaId] || [],
        categoria_id: selectedData?.categoriaId || null
      };
      const response = await axiosClient.put(`/estoque/categoria/editar/${categoriaId}`, dadosAtualizados);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['categorias']);
        setEditing(null);
        setEditedAtributos({});
        setEditedOrdem({});
        enqueueSnackbar('Categoria atualizada com sucesso!', { variant: 'success' });
      },
      onError: (error) => {
        console.error('Erro ao atualizar categoria:', error);
        enqueueSnackbar('Erro ao atualizar categoria', { variant: 'error' });
      }
    }
  );

  const handleSave = (categoriaId: number) => {
    updateCategoria(categoriaId);
  };

  const handleCancel = () => {
    setEditing(null);
    setEditedAtributos({});
    setEditedOrdem({});
  };

  const handleRemoveAtributo = (categoriaId: number, atributoIndex: number) => {
    setEditedAtributos(prev => ({
      ...prev,
      [categoriaId]: prev[categoriaId]?.filter((_, index) => index !== atributoIndex) || []
    }));
  };

  const handleRemoveOrdem = (categoriaId: number, ordemIndex: number) => {
    setEditedOrdem(prev => ({
      ...prev,
      [categoriaId]: prev[categoriaId]?.filter((_, index) => index !== ordemIndex) || []
    }));
  };

  const renderTreeItems = (items: (ICategoria | ISubCategoria)[], parentId?: number) => {
    return items.map((item) => (
      <TreeItem
        key={item.id}
        itemId={item.id.toString()}
        label={
          <Stack direction="row" spacing={1} alignItems="center">
            <FolderIcon fontSize="small" sx={{ color: 'gray' }} />
            <Typography>{item.nome}</Typography>
          </Stack>
        }
      >
        {item.subcategorias && renderTreeItems(item.subcategorias, item.id)}
      </TreeItem>
    ));
  };

  const filteredCategorias = categorias.filter((categoria: any) =>
    categoria.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    categoria.subcategorias.some((sub: any) =>
      sub.nome.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <>
      <DialogCadastrarCategoria
        open={cadastrar}
        onClose={closeDialogCadastrar}
        onSuccess={() => {
          closeDialogCadastrar();
          refetchCategorias();
        }}
      />

      <DialogFullScreen open={open} handleClose={handleClose} title="Listagem de Categorias">
        <DialogTitle align="center">Listagem de Categorias</DialogTitle>

        <Box display="flex" height="80vh" gap={2} p={2}>
          <Paper
            elevation={0}
            sx={{
              width: 300,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                placeholder="Pesquise por grupos"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                  input: { endAdornment: <SearchIcon fontSize="small" /> }
                }}
              />
            </Stack>

            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 1 }}
              size="small"
              onClick={openDialogCadastrar}
              startIcon={<AddIcon fontSize="small" />}
            >
              Cadastrar
            </Button>

            <Divider sx={{ my: 1.5 }} />

            <Box flex={1} overflow="auto">
              {filteredCategorias.length > 0 ? (
                <SimpleTreeView
                  onItemSelectionToggle={(_, itemId) => {
                    setSelected(Number(itemId));
                    setEditing(null);
                  }}
                >
                  {renderTreeItems(filteredCategorias)}
                </SimpleTreeView>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary" textAlign="center">
                    <b>Não há categorias cadastradas.</b> <br />
                    Cadastre uma categoria para continuar.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          <DetalheCategoria
            onSuccess={refetchCategorias}
            selectedData={selectedData}
            editing={editing}
            handleEdit={handleEdit}
            handleSave={handleSave}
            handleCancel={handleCancel}
            handleRemoveAtributo={handleRemoveAtributo}
            handleRemoveOrdem={handleRemoveOrdem}
            editedAtributos={editedAtributos}
            editedOrdem={editedOrdem}
            setEditedAtributos={setEditedAtributos}
            setEditedOrdem={setEditedOrdem}
          />
        </Box>
      </DialogFullScreen>
    </>
  );
}